import { ComponentType, lazy } from "react";
import * as Sentry from "@sentry/react";

// SD-4132 split the bundle, so navigating into a feature area now fetches a
// chunk over the network and any hiccup throws "error loading dynamically
// imported module". With no error boundary in the app that surfaces unhandled
// and leaves a dead screen (FRONT-END-APP-9N: production, /dash, chunk still
// served 200 — a transient fetch failure, not a stale asset).
//
// Reload once and let the fetch retry. The guard matters: if the chunk really
// is gone, reloading again would loop, so a second failure inside the window
// is left to propagate and be reported instead.
const PRELOAD_RELOAD_KEY = "sudonum:preload-reload-at";
const PRELOAD_RELOAD_WINDOW_MS = 10_000;

// Vite calls its handler once per failed dependency, so a navigation that
// loses several assets fires this repeatedly. reload() does not stop the loop
// synchronously, so without this the second failure falls through the guard
// below and is reported as an unhandled error while a reload is already
// committed.
let reloadCommitted = false;

export const isReloadCommitted = () => reloadCommitted;

export const installPreloadReload = (
  reload: () => void = () => window.location.reload(),
) => {
  const onPreloadError = (event: Event) => {
    if (reloadCommitted) {
      event.preventDefault();
      return;
    }

    let lastAttempt = 0;
    try {
      lastAttempt = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY)) || 0;
    } catch {
      // Storage can throw in private modes; treat as "not yet attempted".
    }

    if (Date.now() - lastAttempt < PRELOAD_RELOAD_WINDOW_MS) return;

    // Record the attempt BEFORE suppressing the error. Without a recorded
    // timestamp the retry cannot be bounded, so we must not reload — and having
    // already called preventDefault() by then would leave Vite silently not
    // rethrowing either: no reload, no report, a dead screen nobody hears about.
    try {
      sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
    } catch {
      return;
    }

    event.preventDefault();
    Sentry.addBreadcrumb({
      category: "navigation",
      level: "warning",
      message: "Chunk preload failed; reloading once to retry",
    });

    reloadCommitted = true;
    reload();
  };

  window.addEventListener("vite:preloadError", onPreloadError);

  // The app installs once for the life of the page and never calls this;
  // tests do, so each starts with no listener and no committed reload.
  return () => {
    window.removeEventListener("vite:preloadError", onPreloadError);
    reloadCommitted = false;
  };
};

// preventDefault() above has a side effect the handler alone cannot see: Vite
// only rethrows a failed dynamic import when the event was NOT prevented, so
// once it is, the import resolves to `undefined`. React.lazy then reads
// `.default` off that and throws "Cannot read properties of undefined (reading
// 'default')" before the reload lands (FRONT-END-APP-9Q / 9V / 9W).
//
// While a reload is committed, never settle instead: the Suspense fallback
// stays up until the document is replaced, the same device http.service.ts
// uses for a committed navigation. Anything else that resolves `undefined`
// is a real fault, so it rejects loudly rather than hanging on a dead screen.
export const resolveLazyModule = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> =>
  factory().then((mod) => {
    if (mod) return mod;
    if (reloadCommitted) return new Promise<never>(() => {});
    throw new Error(
      "Lazy chunk resolved to undefined with no reload committed",
    );
  });

export const lazyWithReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) => lazy(() => resolveLazyModule(factory));
